import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Cargo } from '../interfaces/cargo';
import { TravelExpense } from '../interfaces/travel-expense';
import { Utils } from '../resources/utils';
import { SnackBarService } from './snackBar.service';
import { FormMessages } from '../messages/form-messages.enum';
import { Pagination } from '../interfaces/pagination';

export interface CargoClassificationResult {
  availableList: Cargo[];
  unavailableList: (Cargo & { errorMessage?: string })[];
  nonExistentList: number[];
  consecutiveList: string[];
  totalProcessed: number;
  summary: {
    available: number;
    unavailable: number;
    nonExistent: number;
  };
}

export interface CargoValidationFunction {
  (cargo: Cargo): {
    isSelectable: boolean;
    isEnabled: boolean;
    errorMessage?: string;
  };
}

export interface CargoSearchFunction {
  (filters: string, page: number, size: number): Observable<any[]>;
}

export interface CargoClassifierConfig {
  searchMethod: CargoSearchFunction;
  validationMethod?: CargoValidationFunction;
  filters?: string;
  showMessages?: boolean;
  receiveAsList?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CargoClassifierService {

  constructor(
    private utils: Utils,
    private snackbarService: SnackBarService
  ) { }

  public async classifyCargosByConsecutives(
    consecutivesInput: string,
    config: CargoClassifierConfig
  ): Promise<CargoClassificationResult> {
    if (!consecutivesInput || !consecutivesInput.trim()) {
      if (config.showMessages !== false) {
        this.snackbarService.openSnackBar(FormMessages.AT_LEAST_ONE_ELEMENT, undefined, 'alert');
      }
      return this.createEmptyResult();
    }
    const consecutiveList = this.processConsecutiveString(consecutivesInput);
    if (!consecutiveList.length) {
      if (config.showMessages !== false) {
        this.snackbarService.openSnackBar(FormMessages.AT_LEAST_ONE_ELEMENT, undefined, 'alert');
      }
      return this.createEmptyResult();
    }
    const result = await this.processCargos(consecutiveList, config);
    if (config.showMessages !== false && result.nonExistentList.length === consecutiveList.length) {
      this.snackbarService.openSnackBar(
        "Ninguno de los consecutivos está habilitado, vuelve a intentarlo",
        undefined,
        'alert'
      );
    }
    return result;
  }

  public async classifySingleCargo(
    consecutive: string,
    config: CargoClassifierConfig
  ): Promise<{
    cargo?: Cargo;
    status: 'available' | 'unavailable' | 'nonexistent';
    errorMessage?: string;
  }> {
    const result = await this.findCargo(consecutive, config);
    if (!result.found) return { status: 'nonexistent' };

    if (result.available)
      return {
        cargo: result.cargo,
        status: 'available'
      };
    else
      return {
        cargo: result.cargo,
        status: 'unavailable',
        errorMessage: result.errorMessage
      };

  }

  public validateConsecutivesInput(consecutivesInput: string): {
    isValid: boolean;
    consecutiveList: string[];
    errorMessage?: string;
  } {
    if (!consecutivesInput || !consecutivesInput.trim()) {
      return {
        isValid: false,
        consecutiveList: [],
        errorMessage: FormMessages.AT_LEAST_ONE_ELEMENT
      };
    }

    const consecutiveList = this.processConsecutiveString(consecutivesInput);

    return {
      isValid: consecutiveList.length > 0,
      consecutiveList,
      errorMessage: consecutiveList.length === 0 ? FormMessages.AT_LEAST_ONE_ELEMENT : undefined
    };
  }

  private processConsecutiveString(consecutivesInput: string): string[] {
    let cleanInput = consecutivesInput.trim();
    if (cleanInput.endsWith(',')) {
      cleanInput = cleanInput.slice(0, -1);
    }
    // Dividir por comas y limpiar duplicados
    return cleanInput
      .split(",")
      .reduce((arr: string[], consecutive: string) => {
        const trimmed = consecutive.trim();
        if (!arr.includes(trimmed) && trimmed) {
          arr.push(trimmed);
        }
        return arr;
      }, []);
  }

  private async processCargos(
    consecutiveList: string[],
    config: CargoClassifierConfig
  ): Promise<CargoClassificationResult> {

    const availableList: Cargo[] = [];
    const unavailableList: (Cargo & { errorMessage?: string })[] = [];
    const nonExistentList: number[] = [];
    for (const consecutive of consecutiveList) {
      const result = await this.findCargo(consecutive, config);
      if (!result.found) nonExistentList.push(parseInt(consecutive));
      else if (result.available) availableList.push(result.cargo);
      else
        unavailableList.push({
          ...result.cargo,
          errorMessage: result.errorMessage
        });
    }
    return {
      availableList,
      unavailableList,
      nonExistentList,
      consecutiveList,
      totalProcessed: consecutiveList.length,
      summary: {
        available: availableList.length,
        unavailable: unavailableList.length,
        nonExistent: nonExistentList.length
      }
    };
  }

  private async findCargo(
    consecutive: string,
    config: CargoClassifierConfig
  ): Promise<{
    found: boolean;
    available: boolean;
    cargo?: Cargo;
    errorMessage?: string;
  }> {
    try {
      const filtersToSearch: string = config.filters || '';
      const searchQuery = `consecutive=${consecutive}${filtersToSearch}`;
      let cargos: Cargo[] = [];
      if (config.receiveAsList) {
        const results = await config.searchMethod(searchQuery, 1, 3).toPromise() as unknown as Cargo[];
        cargos = results && results.length ? results : [];
      } else {
        const results: { pagination: Pagination, results: Cargo[] } = await config.searchMethod(searchQuery, 1, 3).toPromise() as unknown as { pagination: Pagination, results: Cargo[] };
        cargos = results && results.results ? results.results : [];
      }
      if (!cargos || !cargos.length) return { found: false, available: false };
      let cargo: Cargo = cargos[0];
      // Si no hay validación personalizada, considerar disponible
      if (!config.validationMethod) {
        return {
          found: true,
          available: true,
          cargo
        };
      }
      // Aplicar validación personalizada
      const { isSelectable, isEnabled, errorMessage } = config.validationMethod(cargo);
      return {
        found: true,
        available: isSelectable && isEnabled,
        cargo,
        errorMessage
      };

    } catch (error) {
      console.error('Error finding cargo:', error);
      return { found: false, available: false };
    }
  }

  private createEmptyResult(): CargoClassificationResult {
    return {
      availableList: [],
      unavailableList: [],
      nonExistentList: [],
      consecutiveList: [],
      totalProcessed: 0,
      summary: {
        available: 0,
        unavailable: 0,
        nonExistent: 0
      }
    };
  }
}
